import { useState } from 'react';
import { ShieldX, Wifi, Mail, Phone, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLicenseStore } from '@/stores/licenseStore';

interface LicenseExpiredScreenProps {
  onRetryVerification: () => Promise<void>;
  supportEmail?: string;
  supportPhone?: string;
}

/**
 * Full-screen blocking overlay shown when license expired or grace period ended
 * User cannot access the app until license is verified or renewed
 */
export function LicenseExpiredScreen({
  onRetryVerification,
  supportEmail = 'support@lebusiness.dz',
  supportPhone = '+213 123 456 789',
}: LicenseExpiredScreenProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const { license } = useLicenseStore();

  const handleRetry = async () => {
    setIsVerifying(true);
    try {
      await onRetryVerification();
    } finally {
      setIsVerifying(false);
    }
  };

  const isRevokedOrExpired =
    license?.status === 'expired' || license?.status === 'revoked';

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Logo and header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-red-100 rounded-full">
              <ShieldX className="w-16 h-16 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">leBusiness</h1>
          <p className="text-xl text-red-600 font-semibold">
            Accès bloqué / الوصول محظور
          </p>
        </div>

        {/* Main error message */}
        <Alert className="border-red-300 bg-red-50">
          <AlertDescription>
            <p className="font-bold text-red-900 text-lg mb-3">
              {isRevokedOrExpired
                ? '🚫 Votre licence a expiré ou a été révoquée'
                : '⚠️ Vérification de licence requise'}
            </p>
            <p className="text-red-800 mb-3">
              {isRevokedOrExpired
                ? 'Votre licence n\'est plus valide. Veuillez renouveler votre licence pour continuer à utiliser leBusiness.'
                : 'La période de grâce de votre licence a expiré. Veuillez vous connecter à Internet et vérifier votre licence pour continuer.'}
            </p>
            {license?.revoke_reason && (
              <p className="text-red-700 text-sm mt-2 p-2 bg-red-100 rounded">
                <strong>Raison:</strong> {license.revoke_reason}
              </p>
            )}
          </AlertDescription>
        </Alert>

        {/* License details */}
        {license && (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 space-y-3">
            <h3 className="font-semibold text-gray-900 mb-3">
              Détails de la licence / تفاصيل الترخيص
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Clé de licence:</span>
              </div>
              <div>
                <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                  {license.license_key}
                </code>
              </div>
              <div>
                <span className="text-muted-foreground">Type:</span>
              </div>
              <div>
                <span className="font-medium uppercase">{license.license_type}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Statut:</span>
              </div>
              <div>
                <span className="font-medium text-red-600 uppercase">{license.status}</span>
              </div>
              {license.expires_at && (
                <>
                  <div>
                    <span className="text-muted-foreground">Date d'expiration:</span>
                  </div>
                  <div>
                    <span className="font-medium">
                      {new Date(license.expires_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {!isRevokedOrExpired && (
            <Button
              onClick={handleRetry}
              disabled={isVerifying}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              size="lg"
            >
              {isVerifying ? (
                <>
                  <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Vérification en cours...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Réessayer la vérification
                </>
              )}
            </Button>
          )}

          <div className="text-center text-sm text-gray-600">
            <p className="mb-2">Besoin d'aide ? Contactez notre support:</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href={`mailto:${supportEmail}`}
                className="flex items-center gap-1 text-blue-600 hover:underline"
              >
                <Mail className="w-4 h-4" />
                {supportEmail}
              </a>
              <a
                href={`tel:${supportPhone}`}
                className="flex items-center gap-1 text-blue-600 hover:underline"
              >
                <Phone className="w-4 h-4" />
                {supportPhone}
              </a>
            </div>
          </div>
        </div>

        {/* Why this happens */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">
            Pourquoi cela se produit-il ? / لماذا يحدث هذا؟
          </h4>
          <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
            <li>
              Vérification mensuelle obligatoire pour toutes les licences (même perpétuelles)
            </li>
            <li>Protection contre la contrefaçon et l'utilisation non autorisée</li>
            <li>Garantit la sécurité et la qualité du logiciel pour tous les utilisateurs</li>
            <li>
              Nécessite une connexion Internet pendant quelques secondes seulement
            </li>
          </ul>
          <p className="text-xs text-blue-800 mt-3 text-right leading-relaxed">
            التحقق الشهري الإلزامي لجميع التراخيص • الحماية من التزوير • يضمن الأمان والجودة لجميع
            المستخدمين
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500">
          <p>leBusiness ERP © 2025 - Tous droits réservés</p>
          <p className="mt-1">جميع الحقوق محفوظة</p>
        </div>
      </div>
    </div>
  );
}
